import { useState } from 'react'
import { Modal } from '../components/common/Modal'
import { Button } from '../components/common/Button'
import { InfoTooltip } from '../components/common/InfoTooltip'
import { Input } from '../components/common/Input'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { TOOLTIPS } from '../constants/tooltips'
import { ThesisCard } from '../components/strategy/ThesisCard'
import { RuleSuggestions } from '../components/strategy/RuleSuggestions'
import {
  useTheses,
  usePhilosophies,
  useCreateThesis,
  useUpdateThesis,
  useDeleteThesis,
  useSuggestRules,
  useCreateRule,
} from '../hooks/useThesis'
import type { Thesis, ThesisCreate, ThesisUpdate, ThesisStatus, RuleSuggestion, RuleSuggestionsResponse } from '../types/thesis'
import { STATUS_COLORS } from '../types/thesis'

export function ThesesPage() {
  const { data: theses, isLoading } = useTheses()
  const { data: philosophies } = usePhilosophies()
  const createThesis = useCreateThesis()
  const updateThesis = useUpdateThesis()
  const deleteThesis = useDeleteThesis()
  const suggestRules = useSuggestRules()
  const createRule = useCreateRule()

  // Modal states
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingThesis, setEditingThesis] = useState<Thesis | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<Thesis | null>(null)
  const [suggestionsThesis, setSuggestionsThesis] = useState<Thesis | null>(null)
  const [suggestions, setSuggestions] = useState<RuleSuggestionsResponse | null>(null)
  const [isCreatingRules, setIsCreatingRules] = useState(false)

  // Form state
  const [formData, setFormData] = useState<ThesisCreate>({
    philosophy_id: '',
    name: '',
    narrative: '',
    target_allocation_pct: undefined,
    status: 'active',
  })
  const [error, setError] = useState('')

  // Filter state
  const [statusFilter, setStatusFilter] = useState<ThesisStatus | ''>('')

  const filteredTheses = theses?.filter(
    (t) => !statusFilter || t.status === statusFilter
  )

  const resetForm = () => {
    setFormData({
      philosophy_id: philosophies?.[0]?.id ?? '',
      name: '',
      narrative: '',
      target_allocation_pct: undefined,
      status: 'active',
    })
    setError('')
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    if (!formData.philosophy_id) {
      setError('Please select or create a philosophy first')
      return
    }

    try {
      await createThesis.mutateAsync(formData)
      setIsCreateOpen(false)
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create thesis')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!editingThesis) return

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    try {
      const updateData: ThesisUpdate = {
        name: formData.name,
        narrative: formData.narrative,
        target_allocation_pct: formData.target_allocation_pct,
        status: formData.status,
      }
      await updateThesis.mutateAsync({ id: editingThesis.id, data: updateData })
      setEditingThesis(null)
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update thesis')
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return

    try {
      await deleteThesis.mutateAsync(deleteConfirm.id)
      setDeleteConfirm(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete thesis')
    }
  }

  const handleSuggestRules = async (thesis: Thesis) => {
    setSuggestionsThesis(thesis)
    setSuggestions(null)
    try {
      const result = await suggestRules.mutateAsync(thesis.id)
      setSuggestions(result)
    } catch (err) {
      console.error('Failed to get suggestions:', err)
    }
  }

  const handleCreateRulesFromSuggestions = async (selected: RuleSuggestion[]) => {
    if (!suggestionsThesis) return

    setIsCreatingRules(true)
    try {
      for (const suggestion of selected) {
        await createRule.mutateAsync({
          thesis_id: suggestionsThesis.id,
          name: suggestion.name,
          condition_type: suggestion.condition_type,
          condition_config: suggestion.condition_config,
          priority: suggestion.priority,
        })
      }
      setSuggestionsThesis(null)
      setSuggestions(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create rules')
    } finally {
      setIsCreatingRules(false)
    }
  }

  const openEdit = (thesis: Thesis) => {
    setFormData({
      philosophy_id: thesis.philosophy_id,
      name: thesis.name,
      narrative: thesis.narrative ?? '',
      target_allocation_pct: thesis.target_allocation_pct,
      status: thesis.status,
    })
    setEditingThesis(thesis)
  }

  const openCreate = () => {
    resetForm()
    if (philosophies?.[0]) {
      setFormData((prev) => ({ ...prev, philosophy_id: philosophies[0].id }))
    }
    setIsCreateOpen(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const totalAllocation = theses?.reduce(
    (sum, t) => sum + (t.target_allocation_pct ?? 0),
    0
  ) ?? 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Investment Theses</h1>
          <p className="text-sm text-gray-500 mt-1">
            {theses?.length ?? 0} theses | {totalAllocation}% total allocation
          </p>
        </div>
        <Button onClick={openCreate}>New Thesis</Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <button
          onClick={() => setStatusFilter('')}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
            statusFilter === ''
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {(['active', 'paused', 'archived'] as ThesisStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              statusFilter === status
                ? STATUS_COLORS[status].replace('bg-', 'bg-').replace('text-', 'text-')
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Thesis Grid */}
      {filteredTheses && filteredTheses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTheses.map((thesis) => (
            <ThesisCard
              key={thesis.id}
              thesis={thesis}
              onEdit={openEdit}
              onDelete={setDeleteConfirm}
              onSuggestRules={handleSuggestRules}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No theses yet</p>
          <p className="text-sm text-gray-500 mt-1">Create your first investment thesis</p>
          <Button onClick={openCreate} className="mt-4">
            Create Thesis
          </Button>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create Investment Thesis"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., AI Infrastructure, Quantum Computing"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Philosophy
            </label>
            <select
              value={formData.philosophy_id}
              onChange={(e) => setFormData({ ...formData, philosophy_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {philosophies?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Narrative
            </label>
            <textarea
              value={formData.narrative}
              onChange={(e) => setFormData({ ...formData, narrative: e.target.value })}
              placeholder="Why do you believe in this thesis? What's the investment rationale?"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <Input
            label={<>Target Allocation %<InfoTooltip content={TOOLTIPS.targetAllocation} /></>}
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={formData.target_allocation_pct ?? ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                target_allocation_pct: e.target.value ? parseFloat(e.target.value) : undefined,
              })
            }
            placeholder="e.g., 25"
            id="create-target-allocation"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value as ThesisStatus })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createThesis.isPending}>
              Create
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingThesis}
        onClose={() => setEditingThesis(null)}
        title="Edit Thesis"
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Narrative
            </label>
            <textarea
              value={formData.narrative}
              onChange={(e) => setFormData({ ...formData, narrative: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <Input
            label="Target Allocation %"
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={formData.target_allocation_pct ?? ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                target_allocation_pct: e.target.value ? parseFloat(e.target.value) : undefined,
              })
            }
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value as ThesisStatus })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setEditingThesis(null)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={updateThesis.isPending}>
              Save
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Thesis"
      >
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This will also
          delete all associated rules.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={deleteThesis.isPending}>
            Delete
          </Button>
        </div>
      </Modal>

      {/* AI Suggestions Modal */}
      <RuleSuggestions
        isOpen={!!suggestionsThesis}
        onClose={() => {
          setSuggestionsThesis(null)
          setSuggestions(null)
        }}
        suggestions={suggestions}
        isLoading={suggestRules.isPending}
        onCreateRules={handleCreateRulesFromSuggestions}
        isCreating={isCreatingRules}
      />
    </div>
  )
}
